import { clamp } from "../core/math";
import { smoothSet } from "./envelope";
import { peakOf } from "./meter";

export interface StripState {
  volume: number; // linear 0–1.5
  pan: number; // -1..1
  mute: boolean;
  solo: boolean;
  sendReverb: number; // 0–1
  sendDelay: number; // 0–1
}

export const DEFAULT_STRIP: StripState = { volume: 0.8, pan: 0, mute: false, solo: false, sendReverb: 0.15, sendDelay: 0 };

/** Pure solo/mute rule: any solo → only soloed, unmuted strips are audible. */
export function computeAudible(strips: readonly Pick<StripState, "mute" | "solo">[]): boolean[] {
  const anySolo = strips.some((s) => s.solo);
  return strips.map((s) => !s.mute && (!anySolo || s.solo));
}

/**
 * Channel strip: input → pan → fader → mute/solo gate → main bus + post-gate
 * sends. Nodes are created once; state changes only set AudioParams.
 */
export class ChannelStrip {
  readonly input: GainNode;
  private panner: StereoPannerNode;
  private fader: GainNode;
  private gate: GainNode;
  private reverbSend: GainNode;
  private delaySend: GainNode;
  /** Post-fader, post-mute level tap (what this track contributes to the mix). */
  private meter: AnalyserNode;
  private meterBuf: Float32Array<ArrayBuffer>;
  private _state: StripState;

  constructor(private ctx: BaseAudioContext, main: AudioNode, reverbIn: AudioNode, delayIn: AudioNode, state: Partial<StripState> = {}) {
    this._state = { ...DEFAULT_STRIP, ...state };
    this.input = ctx.createGain();
    this.panner = ctx.createStereoPanner();
    this.fader = ctx.createGain();
    this.gate = ctx.createGain();
    this.reverbSend = ctx.createGain();
    this.delaySend = ctx.createGain();
    this.input.connect(this.panner).connect(this.fader).connect(this.gate);
    this.gate.connect(main);
    this.gate.connect(this.reverbSend).connect(reverbIn);
    this.gate.connect(this.delaySend).connect(delayIn);
    this.meter = ctx.createAnalyser();
    this.meter.fftSize = 1024;
    this.meterBuf = new Float32Array(this.meter.fftSize);
    this.gate.connect(this.meter);
    this.panner.pan.value = clamp(this._state.pan, -1, 1);
    this.fader.gain.value = clamp(this._state.volume, 0, 1.5);
    this.reverbSend.gain.value = clamp(this._state.sendReverb, 0, 1);
    this.delaySend.gain.value = clamp(this._state.sendDelay, 0, 1);
    this.gate.gain.value = this._state.mute ? 0 : 1;
  }

  get state(): StripState { return this._state; }

  update(s: Partial<StripState>): void {
    this._state = { ...this._state, ...s };
    if (s.volume !== undefined) smoothSet(this.fader.gain, clamp(s.volume, 0, 1.5), this.ctx);
    if (s.pan !== undefined) smoothSet(this.panner.pan, clamp(s.pan, -1, 1), this.ctx);
    if (s.sendReverb !== undefined) smoothSet(this.reverbSend.gain, clamp(s.sendReverb, 0, 1), this.ctx);
    if (s.sendDelay !== undefined) smoothSet(this.delaySend.gain, clamp(s.sendDelay, 0, 1), this.ctx);
  }

  /** Sample peak (linear) of the most recent analyser window. */
  readPeak(): number {
    this.meter.getFloatTimeDomainData(this.meterBuf);
    return peakOf(this.meterBuf);
  }

  /** Set by the Mixer from computeAudible(); not part of persisted state. */
  setAudible(audible: boolean, immediate = false): void {
    if (immediate) this.gate.gain.value = audible ? 1 : 0;
    else smoothSet(this.gate.gain, audible ? 1 : 0, this.ctx, 0.005);
  }

  dispose(): void {
    for (const n of [this.input, this.panner, this.fader, this.gate, this.reverbSend, this.delaySend, this.meter]) n.disconnect();
  }
}

export class Mixer {
  private strips = new Map<string, ChannelStrip>();

  constructor(private ctx: BaseAudioContext, private main: AudioNode, private reverbIn: AudioNode, private delayIn: AudioNode) {}

  add(id: string, state: Partial<StripState> = {}): ChannelStrip {
    if (this.strips.has(id)) throw new Error(`Track already exists: ${id}`);
    const strip = new ChannelStrip(this.ctx, this.main, this.reverbIn, this.delayIn, state);
    this.strips.set(id, strip);
    this.recomputeSolo(true);
    return strip;
  }

  get(id: string): ChannelStrip | undefined { return this.strips.get(id); }
  has(id: string): boolean { return this.strips.has(id); }
  ids(): string[] { return [...this.strips.keys()]; }

  update(id: string, s: Partial<StripState>): void {
    const strip = this.strips.get(id);
    if (!strip) return;
    strip.update(s);
    if (s.mute !== undefined || s.solo !== undefined) this.recomputeSolo(false);
  }

  remove(id: string): void {
    this.strips.get(id)?.dispose();
    this.strips.delete(id);
    this.recomputeSolo(false);
  }

  /** Peak per track id. */
  readPeaks(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const [id, s] of this.strips) out[id] = s.readPeak();
    return out;
  }

  private recomputeSolo(immediate: boolean): void {
    const list = [...this.strips.values()];
    computeAudible(list.map((s) => s.state)).forEach((a, i) => list[i]!.setAudible(a, immediate));
  }

  dispose(): void {
    for (const s of this.strips.values()) s.dispose();
    this.strips.clear();
  }
}
